import _ from 'lodash'

import { AbstractState } from '../store/types'
import { ApiRequest } from './api-request'

type MutationFunction = (unknown?) => void
type Commit = (string, unknown?) => void

interface RequestOptions {
  body?: unknown
  files?: object
  startMut?: string | MutationFunction
  successMut?: string | MutationFunction
  errorMut?: string | MutationFunction
  urlParams?: { [key: string]: string | number }
  signal?: AbortSignal
  url?: string
}

export interface ErrorResponse {
  message: string
  status?: number
  rawError?: string
}

function launchMutation(mutation: string | MutationFunction | undefined, commit: Commit, arg?) {
  if (typeof mutation === 'string') {
    return commit(mutation, arg)
  }
  if (_.isFunction(mutation)) {
    return mutation(arg)
  }
  return null
}

const REQUEST_MUTATION = 'setRequest'
export function setRequest(state: AbstractState, request: ApiRequest) {
  state.requests = { ...state.requests, [request.name]: request }
}

class Api {
  static async request(apiRequest: ApiRequest, options: RequestOptions, commit: Commit, retryIfUnlogged = true) {
    try {
      this.startRequest(apiRequest, options.startMut, commit)

      if (!apiRequest.cannotBeDelayed && !this.delayCalls.finished) {
        await this.delayCalls.promise
      }

      let body = null
      // Send data as formdata if files are sent
      if (options.files) {
        body = new FormData()
        for (const [key, file] of Object.entries(options.files)) {
          body.append(key, file)
        }
        if (options.body) {
          body.append('content', JSON.stringify(options.body))
        }
        // Send data as json otherwise
      } else if (options.body) {
        body = JSON.stringify(options.body)
      }

      if (options.url) {
        apiRequest.setUrl(options.url)
      }

      // Disable all AJAX caching, and make explicit the usage of XMLHttpRequest
      const headers = new Headers({
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        expires: '0',
        pragma: 'no-cache',
        'X-Requested-With': 'XMLHttpRequest',
      })
      if (!options.files) {
        headers.append('Content-Type', 'application/json')
      }

      const res = await fetch(apiRequest.getUrl(options.urlParams ?? {}), {
        method: apiRequest.method,
        credentials: 'same-origin' as RequestCredentials,
        headers,
        body,
        signal: options.signal ?? null,
      })

      let responseJson = null
      const responseBody = await res.text()
      if (res.status !== 204) {
        try {
          responseJson = JSON.parse(responseBody)
        } catch {
          // eslint-disable-next-line no-console
          console.warn('Response body was not JSON')
          responseJson = null
        }
      }

      if (res.ok) {
        return this.endSuccessfulRequest(apiRequest, options.successMut, commit, responseJson)
      }

      if (res.status === 401) {
        commit('sessionExpired')

        // this parameter is used to avoid infinite recursion
        if (retryIfUnlogged && !apiRequest.doNotRetry) {
          this.startDelayingCalls()
          try {
            await this.delayCalls.promise
            return this.request(apiRequest, options, commit, false)
          } catch {
            // There is no use handling the request that 401-ed, since the user just cancelled.
            return null
          }
        }
      }

      const errObj: ErrorResponse = { message: 'Une erreur est survenue', status: res.status, rawError: responseJson }
      if (responseJson && responseJson.message) {
        errObj.message = responseJson.message
      }
      return this.endFailedRequest(apiRequest, options.errorMut, commit, errObj)
    } catch (err) {
      if (err.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error(err)
        const errObj = { message: 'Une erreur est survenue.' }
        return this.endFailedRequest(apiRequest, options.errorMut, commit, errObj)
      }
      return null
    }
  }

  static startDelayingCalls() {
    if (this.delayCalls.finished) {
      this.delayCalls.finished = false
      this.delayCalls.promise = new Promise((resolve, reject) => {
        this.delayCalls.resolve = resolve
        this.delayCalls.reject = reject
      })
    }
  }

  static resumeDelayedCalls() {
    if (!this.delayCalls.finished) {
      this.delayCalls.resolve()
      this.delayCalls.finished = true
    }
  }

  static rejectDelayedCalls() {
    if (!this.delayCalls.finished) {
      this.delayCalls.reject('Api call was cancelled')
      this.delayCalls.finished = true
    }
  }

  // Static property, that will contain a Promise
  // if we have to wait before re-firing the API calls.
  static delayCalls = {
    promise: null,
    finished: true,
    resolve: (() => null) as (unknown?) => void,
    reject: (() => null) as (unknown?) => void,
  }

  static startRequest(apiRequest: ApiRequest, startMut: string | MutationFunction, commit: Commit) {
    const requestUpdated = apiRequest.clone()
    requestUpdated.start()
    launchMutation(REQUEST_MUTATION, commit, requestUpdated)
    launchMutation(startMut, commit)
  }

  static endFailedRequest(
    apiRequest: ApiRequest,
    errorMut: string | MutationFunction,
    commit: Commit,
    error: ErrorResponse,
  ) {
    const requestUpdated = apiRequest.clone()
    requestUpdated.end(error)
    launchMutation(REQUEST_MUTATION, commit, requestUpdated)
    return launchMutation(errorMut, commit, error)
  }

  static endSuccessfulRequest(
    apiRequest: ApiRequest,
    mut: string | MutationFunction,
    commit: Commit,
    responseJson: unknown,
  ) {
    const requestUpdated = apiRequest.clone()
    requestUpdated.end()
    launchMutation(REQUEST_MUTATION, commit, requestUpdated)
    return launchMutation(mut, commit, responseJson)
  }
}

export default Api
